import { ProbeResult, REGIONS } from "../types";

interface CachePanelProps {
  results: ProbeResult[];
}

type CacheStatus = string;

const STATUS_COLOR: Record<string, string> = {
  HIT: "#39D98A",
  MISS: "#FDDD48",
  BYPASS: "#FF8C00",
  DYNAMIC: "#FF8C00",
  STALE: "#FDDD48",
  EXPIRED: "#FDDD48",
  UNKNOWN: "#98A1AB",
};

const STATUS_BG: Record<string, string> = {
  HIT: "rgba(57,217,138,0.12)",
  MISS: "rgba(253,221,72,0.12)",
  BYPASS: "rgba(255,140,0,0.12)",
  DYNAMIC: "rgba(255,140,0,0.12)",
  STALE: "rgba(253,221,72,0.12)",
  EXPIRED: "rgba(253,221,72,0.12)",
  UNKNOWN: "rgba(152,161,171,0.1)",
};

const PROVIDER_LABELS: Record<string, string> = {
  cloudflare: "Cloudflare",
  cloudfront: "CloudFront",
  vercel: "Vercel",
  fastly: "Fastly",
  nginx: "Nginx",
  generic: "CDN",
  none: "",
};

function CacheStatusBadge({ status }: { status: CacheStatus }) {
  const color = STATUS_COLOR[status] ?? STATUS_COLOR.UNKNOWN;
  const bg = STATUS_BG[status] ?? STATUS_BG.UNKNOWN;
  return (
    <span style={{
      display: "inline-block",
      fontSize: 11, fontWeight: 700,
      color, background: bg,
      padding: "2px 8px", borderRadius: 3,
      letterSpacing: "0.04em",
    }}>
      {status}
    </span>
  );
}

function formatAge(secs: number | null): string {
  if (secs === null) return "—";
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h`;
}

export default function CachePanel({ results }: CachePanelProps) {
  // Only show results that have a cache provider detected.
  const cacheResults = results.filter((r) => r.cache_provider && r.cache_provider !== "none");

  if (cacheResults.length === 0) {
    // Don't surface as an error — many endpoints are not CDN-backed.
    return (
      <div style={{
        marginTop: 16,
        padding: "10px 14px",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(18,18,18,0.4)",
        fontSize: 12,
        color: "#98A1AB",
      }}>
        No CDN cache headers detected — endpoint may not be served through a recognised CDN.
      </div>
    );
  }

  // Determine the overall provider (first non-none result).
  const provider = cacheResults[0].cache_provider ?? "generic";
  const providerLabel = PROVIDER_LABELS[provider] ?? provider;

  return (
    <div style={{
      marginTop: 16,
      padding: "16px 18px",
      borderRadius: 3,
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(18,18,18,0.5)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          CDN Cache
        </span>
        {providerLabel && (
          <span style={{
            fontSize: 11, fontWeight: 600,
            color: "#98A1AB",
            background: "rgba(255,255,255,0.06)",
            padding: "2px 8px", borderRadius: 3,
          }}>
            {providerLabel}
          </span>
        )}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            {["Region", "Cache Status", "Age", "TTFB"].map((label, i) => (
              <th key={label} style={{
                padding: "6px 10px",
                textAlign: i === 0 ? "left" : "right",
                fontSize: 11, fontWeight: 700,
                color: "#98A1AB",
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cacheResults.map((r) => (
            <tr key={r.region} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <td style={{ padding: "9px 10px", color: "#EFF2F5", fontWeight: 500 }}>
                {REGIONS[r.region] ?? r.region}
              </td>
              <td style={{ padding: "9px 10px", textAlign: "right" }}>
                <CacheStatusBadge status={r.cache_status ?? "UNKNOWN"} />
              </td>
              <td style={{ padding: "9px 10px", textAlign: "right", color: "#98A1AB" }}>
                {formatAge(r.cache_age_secs)}
              </td>
              <td style={{ padding: "9px 10px", textAlign: "right", color: "#98A1AB" }}>
                {r.ttfb_ms != null ? `${r.ttfb_ms}ms` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
